import React, { useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { ArrowRight, ArrowLeft, Shield, Check, X, Mail, KeyRound } from 'lucide-react';
import TurnstileWidget from '@/components/TurnstileWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PASSWORD_RULES = [
  { label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
  { label: 'One number', test: (p) => /\d/.test(p) },
  { label: 'One special character', test: (p) => /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'/~`]/.test(p) },
];

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=email, 2=otp+newpw, 3=success
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpPreview, setOtpPreview] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [honeypot] = useState('');

  const onCaptchaVerify = useCallback((token) => setCaptchaToken(token), []);
  const onCaptchaExpire = useCallback(() => setCaptchaToken(''), []);

  const passwordStrength = useMemo(() => {
    const passed = PASSWORD_RULES.filter(r => r.test(newPassword)).length;
    return { passed, total: PASSWORD_RULES.length, percent: (passed / PASSWORD_RULES.length) * 100 };
  }, [newPassword]);

  const strengthColor = passwordStrength.percent === 100 ? 'bg-green-500' :
    passwordStrength.percent >= 60 ? 'bg-yellow-500' : 'bg-red-400';

  const handleSendOTP = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError('Enter a valid email address'); return; }
    if (!captchaToken) { setError('Please complete the CAPTCHA verification'); return; }
    setError(''); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/otp/send`, { email, captcha_token: captchaToken, honeypot });
      setOtpSent(true);
      if (data.otp_preview) setOtpPreview(data.otp_preview);
      setStep(2);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to send OTP');
    }
    setLoading(false);
  };

  const handleResetPassword = async () => {
    if (!otp || otp.length !== 6) { setError('Enter the 6-digit OTP'); return; }
    if (passwordStrength.passed < passwordStrength.total) { setError('Password does not meet requirements'); return; }
    setError(''); setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, {
        email, otp, new_password: newPassword,
        captcha_token: captchaToken, honeypot
      });
      setStep(3);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to reset password');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#FFFEF9] flex items-center justify-center px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-12 h-12 rounded-lg overflow-hidden flex items-center justify-center">
              <img src="/bb-logo.jpeg" alt="BuildBotics" className="w-full h-full object-contain" />
            </div>
          </Link>
          <h1 className="text-2xl font-bold text-[#1a1a2e]" style={{ fontFamily: 'Outfit' }}>Reset Password</h1>
          <p className="text-sm text-gray-500 mt-1">We'll verify your email with an OTP to reset your password</p>
        </div>

        <div className="neo-card-static p-8">
          {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-lg mb-4" data-testid="forgot-error">{error}</div>}

          {/* Honeypot */}
          <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
            <input type="text" name="fax_number" tabIndex={-1} autoComplete="new-password"
              value="" onChange={() => {}} readOnly />
          </div>

          {step === 1 && (
            <div className="space-y-5" data-testid="forgot-step-email">
              <div className="w-14 h-14 rounded-full bg-cyan-50 border-2 border-cyan-200 flex items-center justify-center mx-auto mb-2">
                <Mail className="w-6 h-6 text-[#0891B2]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#1a1a2e] mb-1.5 block">Email Address</label>
                <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter your registered email"
                  className="bg-[#FEFCE8] border-gray-200 text-[#1a1a2e] focus:border-[#0891B2] focus:ring-[#0891B2] h-11" data-testid="forgot-email-input" />
              </div>
              <TurnstileWidget onVerify={onCaptchaVerify} onExpire={onCaptchaExpire} />
              <button onClick={handleSendOTP} disabled={loading}
                className="btn-cyan w-full flex items-center justify-center gap-2 text-sm !rounded-xl" data-testid="forgot-send-otp-btn">
                {loading ? 'Sending OTP...' : <><span>Send OTP</span> <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="forgot-step-reset">
              <div className="w-14 h-14 rounded-full bg-cyan-50 border-2 border-cyan-200 flex items-center justify-center mx-auto mb-2">
                <KeyRound className="w-6 h-6 text-[#0891B2]" />
              </div>

              {otpPreview && (
                <div className="bg-cyan-50 border border-cyan-200 text-cyan-700 text-sm px-4 py-3 rounded-lg" data-testid="forgot-otp-preview">
                  <i className="fa-solid fa-info-circle mr-2" />Demo OTP: <strong>{otpPreview}</strong>
                </div>
              )}
              {!otpPreview && (
                <div className="bg-green-50 border border-green-200 text-green-700 text-sm px-4 py-3 rounded-lg" data-testid="forgot-otp-sent-msg">
                  <i className="fa-solid fa-envelope-circle-check mr-2" />OTP sent to <strong>{email}</strong>. Check inbox &amp; spam.
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-[#1a1a2e] mb-1.5 block">6-Digit OTP</label>
                <Input type="text" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000" maxLength={6}
                  className="bg-[#FEFCE8] border-gray-200 text-[#1a1a2e] focus:border-[#0891B2] focus:ring-[#0891B2] h-11 text-center text-2xl tracking-[0.5em] font-mono" data-testid="forgot-otp-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#1a1a2e] mb-1.5 block">New Password</label>
                <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                  placeholder="Min. 8 chars, uppercase, number, special"
                  className="bg-[#FEFCE8] border-gray-200 text-[#1a1a2e] focus:border-[#0891B2] focus:ring-[#0891B2] h-11" data-testid="forgot-new-password-input" />
                {newPassword && (
                  <>
                    <div className="mt-2" data-testid="forgot-password-strength">
                      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className={`h-full ${strengthColor} transition-all duration-300 rounded-full`}
                          style={{ width: `${passwordStrength.percent}%` }} />
                      </div>
                    </div>
                    <div className="mt-2 space-y-1">
                      {PASSWORD_RULES.map((r, i) => {
                        const pass = r.test(newPassword);
                        return (
                          <div key={i} className={`flex items-center gap-2 text-xs ${pass ? 'text-green-600' : 'text-gray-400'}`}>
                            {pass ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                            <span>{r.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
              <button onClick={handleResetPassword} disabled={loading}
                className="btn-cyan w-full flex items-center justify-center gap-2 text-sm !rounded-xl" data-testid="forgot-reset-btn">
                {loading ? 'Resetting...' : <><Shield className="w-4 h-4" /><span>Reset Password</span></>}
              </button>
              <button onClick={() => { setStep(1); setOtpSent(false); setOtp(''); setNewPassword(''); setError(''); }}
                className="w-full text-center text-sm text-gray-500 hover:text-[#0891B2] flex items-center justify-center gap-1 mt-2">
                <ArrowLeft className="w-3 h-3" /> Back to email
              </button>
            </div>
          )}

          {step === 3 && (
            <div className="text-center py-6" data-testid="forgot-success">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-bold text-[#1a1a2e] mb-2">Password Reset!</h3>
              <p className="text-sm text-gray-500 mb-6">Your password has been updated successfully.</p>
              <button onClick={() => navigate('/login')}
                className="btn-cyan flex items-center justify-center gap-2 text-sm !rounded-xl mx-auto" data-testid="forgot-goto-login-btn">
                <span>Go to Login</span> <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-sm text-gray-500 mt-6">
          Remember your password?{' '}
          <Link to="/login" className="text-[#0891B2] hover:underline font-medium">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
